import React from 'react';
import { ArrowRight, Lock, Users } from 'lucide-react';
import { BakeryEvent } from '../types/event';
import { EventMenu } from './EventMenu';
interface EventRowProps {
  event: BakeryEvent;
  onSignup: (event: BakeryEvent) => void;
}
export function EventRow({
  event,
  onSignup
}: EventRowProps) {
  const date = new Date(`${event.date}T12:00:00`);
  const weekday = date.toLocaleDateString('sv-SE', {
    weekday: 'long'
  });
  const shortDate = `${date.getDate()}/${date.getMonth() + 1}`;
  if (event.locked) {
    return <article className="relative overflow-hidden rounded-2xl bg-[#1C1917]" aria-label={event.lockedText ?? 'Kommer snart'} data-id="element-470" data-mp="1ksxdr30xfbiu">
        <div aria-hidden="true" inert className="grid grid-cols-1 sm:grid-cols-[120px_1fr] gap-x-6 gap-y-3 p-5 md:p-6 blur-[6px] opacity-50 select-none pointer-events-none" data-id="element-471" data-mp="1fum4atejn315">
          <div className="flex sm:flex-col items-baseline sm:items-start sm:justify-center gap-2 sm:gap-0" data-id="element-472" data-mp="3vk70paus3k0z">
            <span className="text-sm text-[#F5EDE3]/60 capitalize" data-id="element-473" data-mp="1ukrpdkxswlr8">
              {weekday}
            </span>
            <span className="text-3xl md:text-4xl font-extrabold text-[#FFB27A] leading-none tracking-tight" data-id="element-474" data-mp="1zkntdk6i0d6s">
              {shortDate}
            </span>
          </div>
          <div className="min-w-0" data-id="element-475" data-mp="1pm7z58c6cnt2">
            {/* Placeholder shapes only – the real event stays secret */}
            <div className="h-7 w-48 rounded-md bg-[#F5EDE3]/70 mb-3" data-id="element-476" data-mp="1bv34ylrksnq4" />
            <div className="space-y-2" data-id="element-477" data-mp="1bsyv7n6zwuhx">
              <div className="h-3.5 w-full rounded bg-[#F5EDE3]/40" data-id="element-478" data-mp="1glrxm4ezrqqu" />
              <div className="h-3.5 w-11/12 rounded bg-[#F5EDE3]/40" data-id="element-479" data-mp="11wni4uzl00mf" />
              <div className="h-3.5 w-2/3 rounded bg-[#F5EDE3]/40" data-id="element-480" data-mp="11ygnm6ygkesu" />
            </div>
            <div className="mt-5 flex sm:justify-end" data-id="element-481" data-mp="16w8dywjgfsux">
              <span className="h-10 w-32 rounded-full bg-[#FF7A1A]" data-id="element-482" data-mp="svfio9lbwtqr" />
            </div>
          </div>
        </div>
        <div className="absolute inset-0 flex flex-col items-center justify-center gap-3 px-6 text-center bg-[#0E0C0B]/40" data-id="element-483" data-mp="wxsoey02srnj">
          <span className="w-11 h-11 rounded-full bg-[#0E0C0B] border border-[#FF7A1A]/60 flex items-center justify-center" data-id="element-484" data-mp="2zlsmf1dq2yyq">
            <Lock className="w-5 h-5 text-[#FF9A4D]" aria-hidden="true" data-id="element-485" data-mp="2urodwdot6ivj" />
          </span>
          <p className="halloween-display text-2xl md:text-3xl text-[#FF9A4D] tracking-wide leading-tight" data-id="element-486" data-mp="2psbrtv5rhfu0">
            {event.lockedText ?? 'Kommer snart'}
          </p>
        </div>
      </article>;
  }
  return <article className="grid grid-cols-1 sm:grid-cols-[120px_1fr] gap-x-6 gap-y-3 bg-[#1C1917] rounded-2xl p-5 md:p-6" data-id="element-487" data-mp="3djzcvwgcdwod">
      <div className="flex sm:flex-col items-baseline sm:items-start sm:justify-center gap-2 sm:gap-0" data-id="element-488" data-mp="38kcy3ikv88dg">
        <span className="text-sm text-[#F5EDE3]/60 capitalize" data-id="element-489" data-mp="34ju7tsymfadd">{weekday}</span>
        <span className="text-3xl md:text-4xl font-extrabold text-[#FFB27A] leading-none tracking-tight" data-id="element-490" data-mp="1x5qy4o1tcrna">
          {shortDate}
        </span>
      </div>

      <div className="min-w-0" data-id="element-491" data-mp="1t2knz5bkygbj">
        <div className="flex flex-wrap items-start justify-between gap-x-4 gap-y-2 mb-2" data-id="element-492" data-mp="1t6i55lelkxss">
          <h3 className="text-xl md:text-2xl font-bold text-[#F5EDE3] leading-tight" data-id="element-493" data-mp="8ka398afsi7q">
            {event.title}
          </h3>
          {(event.time || event.secondaryTime) && <div className="flex flex-wrap gap-2 shrink-0" data-id="element-494" data-mp="hlgmhp4h074o">
              {event.time && <span className="px-3 py-1 rounded-full bg-[#FF7A1A] text-[#1A0F08] text-sm font-bold whitespace-nowrap" data-id="element-495" data-mp="mf2tp1rv7it3">
                  {event.time}
                </span>}
              {event.secondaryTime && <span className="px-3 py-1 rounded-full border border-[#FF7A1A] text-[#FFB27A] text-sm font-bold whitespace-nowrap" data-id="element-496" data-mp="10d9g9lrd5wdg">
                  {event.secondaryTime}
                </span>}
            </div>}
        </div>

        <p className="text-[#F5EDE3]/80 leading-relaxed" data-id="element-497" data-mp="325xlg0nkitvv">{event.description}</p>

        {event.highlight && <p className="mt-4 rounded-xl bg-[#2A1D14] px-4 py-3 text-[#F5EDE3]/85 leading-relaxed" data-id="element-498" data-mp="373z62q0n719g">
            <span className="font-bold text-[#FF9A4D]" data-id="element-499" data-mp="2brplh1gl3jlf">
              {event.highlight.title}
            </span>{' '}
            – {event.highlight.text}
          </p>}

        {event.menu && <EventMenu id={event.id} menu={event.menu} data-id="element-500" data-mp="2lr849ekrczbw" />}

        {event.dropIn && !event.signup && <div className="mt-5 flex sm:justify-end" data-id="element-501" data-mp="2gt6kerficvzd">
            <span className="inline-flex items-center px-5 py-2.5 rounded-full border border-[#FF7A1A] text-[#FFB27A] text-sm font-bold whitespace-nowrap" data-id="element-502" data-mp="1t2ja5zi4du9n">
              Drop in
            </span>
          </div>}

        {event.signup && <div className="mt-5 flex flex-wrap items-center gap-x-4 gap-y-3 sm:justify-end" data-id="element-503" data-mp="22u6xppbk2r5u">
            <span className="inline-flex items-center gap-1.5 text-sm font-semibold text-[#FFB27A]" data-id="element-504" data-mp="1y0kq4bk91dgw">
              <Users className="w-4 h-4" aria-hidden="true" data-id="element-505" data-mp="1aaqhy7avkd2z" />
              Begränsat antal platser
            </span>
            <button onClick={() => onSignup(event)} className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full bg-[#FF7A1A] text-[#1A0F08] text-sm font-bold hover:bg-[#FF9A4D] transition-colors whitespace-nowrap focus:outline-none focus-visible:ring-2 focus-visible:ring-[#FFB27A] focus-visible:ring-offset-2 focus-visible:ring-offset-[#1C1917]" data-id="element-506" data-mp="15bnnfgsitp99">
              {event.ctaLabel ?? 'Boka plats'}
              <ArrowRight className="w-4 h-4" aria-hidden="true" data-id="element-507" data-mp="3lnscbx0tslm" />
            </button>
          </div>}

        {event.footnote && <p className="mt-5 pt-4 border-t border-[#F5EDE3]/10 text-sm text-[#F5EDE3]/60 leading-relaxed" data-id="element-508" data-mp="8k8pu3kw1y6d">
            {event.footnote}
          </p>}
      </div>
    </article>;
}